import {
  type FormEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { FidsCoordinate } from 'contracts/FidsTrackingRoutes';
import {
  loadGoogleMaps,
  type GoogleMapCircle,
  type GoogleMapInstance,
  type GoogleMapLatLng,
  type GoogleMapListener,
  type GoogleMapMarker,
  type GoogleMapPolyline,
  type GoogleMapsApi,
  type GoogleMapPoint,
} from './googleMaps';
import { formatKm } from './format';
import { Colors } from 'styles/constants';

const EARTH_RADIUS_METERS = 6_371_008.8;

type MapCanvasProps = {
  apiKey: string;
  center: FidsCoordinate | null;
  // Raios dos anéis em volta do centro, do menor (a chegada) para fora.
  rings: number[];
  // Com `picking`, um clique no mapa marca o centro, e o centro e os anéis podem ser arrastados.
  picking: boolean;
  pickingHelp: string | null;
  onPick: (point: FidsCoordinate) => void;
  // Fica sobre o canto inferior esquerdo do mapa (a legenda dos anéis).
  overlay?: ReactNode;
};

// Mensagem da busca a partir do código de erro do Google (`MapsRequestError.code`).
function searchErrorMessage(error: unknown): string {
  const code = (error as { code?: string } | null)?.code;
  if (code === 'ZERO_RESULTS')
    return 'Nenhum lugar encontrado para essa busca.';
  if (code === 'REQUEST_DENIED') {
    return 'A busca de endereços não está liberada na chave do Google Maps. Navegue pelo mapa.';
  }
  return 'Não foi possível buscar agora. Tente de novo ou navegue pelo mapa.';
}

function mapPoint(point: FidsCoordinate): GoogleMapPoint {
  return { lat: point.latitude, lng: point.longitude };
}

// Fundo branco da etiqueta do raio, centrado no ponto do anel (cabe "300 km").
const RING_LABEL_PILL =
  'M -28,-10 H 28 A 10 10 0 0 1 28 10 H -28 A 10 10 0 0 1 -28 -10 Z';

function coordinateOf(latLng: GoogleMapLatLng): FidsCoordinate {
  return { latitude: latLng.lat(), longitude: latLng.lng() };
}

// O radar desenhado: o círculo de chegada, os anéis tracejados, os rótulos e o marcador do
// centro. `moveTo` leva tudo junto para outro centro enquanto a pessoa arrasta.
type RadarOverlays = {
  arrival: GoogleMapCircle | null;
  outerRings: GoogleMapPolyline[];
  labels: GoogleMapMarker[];
  marker: GoogleMapMarker;
  listeners: GoogleMapListener[];
};

// Ponto a `distanceMeters` do centro, na direção `bearingDegrees` (0 = norte).
function pointAt(
  center: FidsCoordinate,
  distanceMeters: number,
  bearingDegrees: number
): GoogleMapPoint {
  const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
  const toDegrees = (radians: number) => (radians * 180) / Math.PI;
  const latitude = toRadians(center.latitude);
  const longitude = toRadians(center.longitude);
  const angularDistance = distanceMeters / EARTH_RADIUS_METERS;
  const bearing = toRadians(bearingDegrees);
  const pointLatitude = Math.asin(
    Math.sin(latitude) * Math.cos(angularDistance) +
      Math.cos(latitude) * Math.sin(angularDistance) * Math.cos(bearing)
  );
  const pointLongitude =
    longitude +
    Math.atan2(
      Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(latitude),
      Math.cos(angularDistance) - Math.sin(latitude) * Math.sin(pointLatitude)
    );
  return { lat: toDegrees(pointLatitude), lng: toDegrees(pointLongitude) };
}

// Pontos de um círculo geodésico, para os anéis tracejados.
function circlePath(
  center: FidsCoordinate,
  radiusMeters: number
): GoogleMapPoint[] {
  return Array.from({ length: 97 }, (_, index) =>
    pointAt(center, radiusMeters, (index * 360) / 96)
  );
}

// Enquadra o anel de fora inteiro.
function fitRings(
  maps: GoogleMapsApi,
  map: GoogleMapInstance,
  center: FidsCoordinate,
  rings: number[]
) {
  const bounds = new maps.LatLngBounds();
  circlePath(center, Math.max(...rings, 1000)).forEach((point) =>
    bounds.extend(point)
  );
  map.fitBounds(bounds, 48);
}

function clearRadar(radar: RadarOverlays | null) {
  if (!radar) return;
  radar.listeners.forEach((listener) => listener.remove());
  [radar.arrival, ...radar.outerRings, ...radar.labels, radar.marker].forEach(
    (overlay) => overlay?.setMap(null)
  );
}

export const MapCanvas = ({
  apiKey,
  center,
  rings,
  picking,
  pickingHelp,
  onPick,
  overlay,
}: MapCanvasProps) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<GoogleMapInstance | null>(null);
  const mapsRef = useRef<GoogleMapsApi | null>(null);
  const radarRef = useRef<RadarOverlays | null>(null);
  // Enquadra só quando o centro aparece ou os anéis mudam; mover o centro mantém o zoom.
  const framedRef = useRef<{ hasCenter: boolean; ringsKey: string }>({
    hasCenter: false,
    ringsKey: '',
  });
  const pickingRef = useRef(picking);
  const callbackRef = useRef(onPick);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  useEffect(() => {
    pickingRef.current = picking;
    callbackRef.current = onPick;
  }, [picking, onPick]);

  // A mira mostra que o próximo clique marca o centro.
  useEffect(() => {
    if (ready)
      mapRef.current?.setOptions({
        draggableCursor: picking ? 'crosshair' : null,
      });
  }, [ready, picking]);

  useEffect(() => {
    let cancelled = false;
    let clickListener: GoogleMapListener | null = null;
    void loadGoogleMaps(apiKey)
      .then((maps) => {
        if (cancelled || !elementRef.current) return;
        mapsRef.current = maps;
        const map = new maps.Map(elementRef.current, {
          center: { lat: -15.78, lng: -47.93 },
          zoom: 4,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          clickableIcons: false,
          gestureHandling: 'greedy',
        });
        mapRef.current = map;
        clickListener = map.addListener('click', (event) => {
          if (!pickingRef.current || !event.latLng) return;
          callbackRef.current({
            latitude: event.latLng.lat(),
            longitude: event.latLng.lng(),
          });
        });
        setReady(true);
      })
      .catch((loadError: unknown) => {
        if (cancelled) return;
        setError(
          loadError instanceof Error
            ? loadError.message
            : 'Não foi possível carregar o Google Maps.'
        );
      });

    return () => {
      cancelled = true;
      clickListener?.remove();
      clearRadar(radarRef.current);
      radarRef.current = null;
      mapRef.current = null;
      mapsRef.current = null;
    };
  }, [apiKey]);

  // Desenha o centro e os anéis: o de chegada preenchido, os outros tracejados.
  const ringsKey = rings.join();
  useEffect(() => {
    const map = mapRef.current;
    const maps = mapsRef.current;
    if (!ready || !map || !maps) return;
    clearRadar(radarRef.current);
    radarRef.current = null;
    if (!center) {
      framedRef.current = { hasCenter: false, ringsKey };
      return;
    }

    // `Colors` em vez de `ColorScheme`: o Google Maps exige `string`.
    const [arrivalRadius, ...outerRadii] = rings;
    const arrival = arrivalRadius
      ? new maps.Circle({
          map,
          center: mapPoint(center),
          radius: arrivalRadius,
          strokeColor: Colors.Orange,
          strokeOpacity: 0.9,
          strokeWeight: 2,
          fillColor: Colors.Orange,
          fillOpacity: 0.15,
          clickable: true,
          draggable: pickingRef.current,
        })
      : null;
    const outerRings = outerRadii.map(
      (ring) =>
        new maps.Polyline({
          map,
          path: circlePath(center, ring),
          strokeOpacity: 0,
          clickable: false,
          icons: [
            {
              icon: {
                path: 'M 0,-1 0,1',
                strokeColor: Colors.Gray60,
                strokeOpacity: 0.8,
                scale: 2,
              },
              offset: '0',
              repeat: '12px',
            },
          ],
        })
    );
    // Uma etiqueta branca no topo de cada anel tracejado. O de chegada não leva etiqueta: ele
    // é o laranja, explicado na legenda, e uma etiqueta perto do marcador o cobriria.
    const labels = outerRadii.map(
      (ring) =>
        new maps.Marker({
          map,
          position: pointAt(center, ring, 0),
          clickable: false,
          label: {
            text: formatKm(ring),
            color: Colors.Gray70,
            fontSize: '12px',
            fontWeight: '700',
          },
          icon: {
            path: RING_LABEL_PILL,
            scale: 1,
            fillColor: Colors.White,
            fillOpacity: 0.95,
            strokeColor: Colors.Gray40,
            strokeWeight: 1,
          },
        })
    );
    const marker = new maps.Marker({
      map,
      position: mapPoint(center),
      title: 'Centro do rastreio',
      draggable: pickingRef.current,
    });

    // Enquanto arrasta, os anéis e os rótulos acompanham; ao soltar, vale o centro novo.
    const moveTo = (next: FidsCoordinate) => {
      marker.setPosition(mapPoint(next));
      outerRings.forEach((ring, index) =>
        ring.setPath(circlePath(next, outerRadii[index]))
      );
      labels.forEach((label, index) =>
        label.setPosition(pointAt(next, outerRadii[index], 0))
      );
    };
    const pick = (event: { latLng: GoogleMapLatLng | null }) => {
      if (pickingRef.current && event.latLng) {
        callbackRef.current(coordinateOf(event.latLng));
      }
    };
    const listeners = [
      marker.addListener('drag', () => {
        const next = coordinateOf(marker.getPosition());
        arrival?.setCenter(mapPoint(next));
        moveTo(next);
      }),
      marker.addListener('dragend', pick),
    ];
    if (arrival) {
      listeners.push(
        arrival.addListener('drag', () =>
          moveTo(coordinateOf(arrival.getCenter()))
        ),
        arrival.addListener('dragend', () => {
          if (pickingRef.current)
            callbackRef.current(coordinateOf(arrival.getCenter()));
        }),
        // O círculo de chegada cobre o mapa: um clique nele também marca o centro.
        arrival.addListener('click', pick)
      );
    }
    radarRef.current = { arrival, outerRings, labels, marker, listeners };

    const framed = framedRef.current;
    if (!framed.hasCenter || framed.ringsKey !== ringsKey) {
      fitRings(maps, map, center, rings);
    }
    framedRef.current = { hasCenter: true, ringsKey };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, center?.latitude, center?.longitude, ringsKey]);

  // O centro e os anéis só podem ser arrastados no passo do centro.
  useEffect(() => {
    radarRef.current?.marker.setDraggable(picking);
    radarRef.current?.arrival?.setDraggable(picking);
  }, [picking, ready, center?.latitude, center?.longitude, ringsKey]);

  const searchPlace = async (event: FormEvent) => {
    event.preventDefault();
    const maps = mapsRef.current;
    const map = mapRef.current;
    if (!maps || !map || !search.trim()) return;
    setIsSearching(true);
    setSearchError(null);
    try {
      const { results } = await new maps.Geocoder().geocode({
        address: search.trim(),
        region: 'BR',
      });
      const place = results[0];
      if (!place)
        throw Object.assign(new Error('Sem resultado'), {
          code: 'ZERO_RESULTS',
        });
      if (place.geometry.viewport) {
        map.fitBounds(place.geometry.viewport, 0);
      } else {
        map.setCenter({
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        });
        map.setZoom(12);
      }
    } catch (searchFailure) {
      setSearchError(searchErrorMessage(searchFailure));
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="relative min-h-[360px] flex-1 overflow-hidden rounded border border-slate-300 bg-slate-100">
      <div ref={elementRef} className="absolute inset-0" />
      {!ready && !error ? (
        <div
          role="status"
          className="absolute inset-0 flex items-center justify-center text-sm text-slate-600"
        >
          Carregando mapa...
        </div>
      ) : null}
      {error ? (
        <div
          role="alert"
          className="absolute inset-x-3 top-3 rounded border border-amber-300 bg-white p-3 text-sm text-slate-700 shadow"
        >
          {error}
        </div>
      ) : null}
      {ready && !error ? (
        <div className="absolute left-3 right-16 top-3 flex flex-col gap-1">
          <form
            role="search"
            onSubmit={(event) => void searchPlace(event)}
            className="flex max-w-xl gap-2"
          >
            <input
              type="search"
              aria-label="Buscar cidade ou endereço"
              placeholder="Buscar cidade ou endereço"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="h-10 min-w-0 flex-1 rounded border border-slate-300 bg-white px-3 text-sm shadow outline-none"
            />
            <button
              type="submit"
              disabled={isSearching || !search.trim()}
              className="h-10 rounded border border-slate-300 bg-white px-3 text-xs font-bold text-slate-700 shadow hover:bg-slate-50 disabled:opacity-60"
            >
              {isSearching ? 'Buscando...' : 'Buscar'}
            </button>
            {center ? (
              <button
                type="button"
                onClick={() => {
                  if (mapsRef.current && mapRef.current) {
                    fitRings(mapsRef.current, mapRef.current, center, rings);
                  }
                }}
                className="h-10 rounded border border-slate-300 bg-white px-3 text-xs font-bold text-slate-700 shadow hover:bg-slate-50"
              >
                Ver anéis
              </button>
            ) : null}
          </form>
          {searchError ? (
            <p
              role="alert"
              className="max-w-xl rounded border border-amber-300 bg-white px-3 py-2 text-xs text-slate-700 shadow"
            >
              {searchError}
            </p>
          ) : null}
          {/* Abaixo da busca, para não cobrir a legenda dos anéis. */}
          {pickingHelp ? (
            <p
              role="status"
              className="max-w-xl rounded border border-orange-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow"
            >
              {pickingHelp}
            </p>
          ) : null}
        </div>
      ) : null}
      {overlay}
    </div>
  );
};
